/**
 * SC-209 phase 4: picture-in-picture on Safari, which is one `<video>` element and nothing else.
 *
 * Safari has never shipped Document Picture-in-Picture. What it has is the original single-video API, so
 * there is no second document, no tile grid, no toolbar and no whiteboard — `getSurface()` returns `null`
 * forever and React never renders into anything. The model still arrives on every change; it is collapsed to
 * a single picture by `pickSafariPicture` and that picture is what the floating window shows.
 *
 * **The element is attached and playing long before anyone clicks, and that is the whole design.** Measured
 * on Safari 18.4 (2026-09-20): assigning `srcObject` resets `readyState` to 0, and `requestPictureInPicture()`
 * on an element in that state throws `InvalidStateError: The video element is not ready to enter the
 * Picture-in-Picture mode`. You cannot wait for metadata inside the click either, because the await ends the
 * transient activation Safari requires. So the only order that works is: attach on every `update()`, and let
 * `open()` do nothing but ask. That is why `PipRenderer.update()` is documented as being called while closed.
 *
 * **SC-158: a camera held for its background is never shown here.** There is no cover to lay over a bare
 * video, so `pickSafariPicture` leaves those cameras out, and what is shown instead depends on whether anyone
 * is looking (SC-476, below). The one exception to "never" is our own camera during a None-to-effect hold,
 * by the user's decision of 2026-10-08 that no picture-in-picture pauses the video: it is shown as a
 * local-only clone of the live camera kept from just before the hold (`holdPreviewTrackRef`), never
 * published, until the effect's first frame.
 *
 * **Never mirrored (SC-476).** The in-page tile, the join screen and the Document PiP window show our own
 * camera mirrored, by CSS on the `<video>` (`utils/selfViewMirror`). This window is Safari's own surface and
 * takes no CSS from the page, and flipping the frames themselves would mean publishing or re-encoding a
 * different track, which the self view must never do. So in Safari's native window our own camera reads
 * unmirrored, and the model deliberately has no mirror flag for it.
 *
 * **SC-476: while the window is open its source is never empty, and a swap never goes through empty.**
 * The likely cause of the reported "jump to the bottom-right, then gone" (inferred from the reports and the
 * code, not reproduced on a device): Safari closes the window when its source empties, animating it back
 * to this element, which sits in the bottom-right corner. A swap used to empty it, twice (LiveKit's
 * `detach` and an explicit clear) before attaching again. So a swap now adds the new track to the stream
 * already on the element before the old one leaves it (`syncTrack`). And where there would be nothing to
 * show, the window gets a neutral picture (`neutralPicture`): the held person's place is kept for the
 * length of their hold (`pickSafariPicture`) rather than handed to someone else, and "nobody on camera" is
 * a caption instead of a blank. Closed, nothing changes: no window can close, so an empty element is still
 * just a disabled button. "Open" includes the moment between asking and `enterpictureinpicture`: a model
 * update in that gap takes the open rules too, or it would empty the element under a window that is appearing.
 *
 * **The window can also close without telling us.** `leavepictureinpicture` is the signal, but users
 * reported (2026-10-24) a window that closed by itself while the button went on saying "Return video to
 * tab" and a click did nothing. Whatever stops the event, the code had no way back: `close()` asked Safari
 * to exit a window that was not there, the request was refused, and nothing settled `isOpen`. So that
 * refusal settles it, and `webkitpresentationmodechanged` (and the tab becoming visible again) prompt a
 * re-check once the browser has had time to agree with itself. A click that finds the window already gone
 * reopens it in the same click, so what swaps the picture after that settle waits a microtask: the element
 * stays untouched until `requestPictureInPicture()` has been called.
 *
 * Everything here was measured rather than assumed — probes P2a, P2b, P2c and P3 on Safari 18.4 / macOS
 * 15.4.1. The two findings that shaped the file are the attach-before-gesture rule above, and that the
 * standard `enterpictureinpicture`/`leavepictureinpicture` events are the only trustworthy signal:
 * `webkitpresentationmodechanged` fires while `document.pictureInPictureElement` still disagrees, in both
 * directions, so it never decides anything by itself (see `recheckSoon`).
 */

import type { TrackReference } from '@livekit/components-react'

import { CAMERA_WAITING_RELEASE_GRACE_MS } from '../../../../services/liveKitMediaStatusStore'
import { pickSafariPicture } from '../pipModel'
import type { PipModel, SafariPick } from '../pipModel'
import { createNeutralPictures } from './neutralPicture'
import { PipElementSource } from './pipElementSource'
import type { NeutralPictures } from './neutralPicture'
import type { PipClosedBy, PipOpenReason, PipRenderer, PipSurface } from './PipRenderer'
import { streamTrackRef } from './streamTrackRef'
import type { AttachableTrack } from './streamTrackRef'

/**
 * Small, almost transparent, out of the way — but genuinely laid out.
 *
 * Safari refuses picture-in-picture on an element that is not rendered, so `display: none`, `visibility:
 * hidden` and a zero size are all out. P2c confirmed this exact shape is accepted on 18.4. It is 2px rather
 * than 1px because a subpixel-rounded 1px box can collapse to nothing on a scaled display.
 */
const HIDDEN_VIDEO_STYLE: Partial<CSSStyleDeclaration> = {
  position: 'fixed',
  right: '0px',
  bottom: '0px',
  width: '2px',
  height: '2px',
  opacity: '0.01',
  pointerEvents: 'none',
  zIndex: '-1',
}

/**
 * How long a remote camera that went off may be a hold whose "waiting" message is still on its way. The
 * same two messages, the same transports and the same skew the media-status store already allows when a hold
 * ends (`CAMERA_WAITING_RELEASE_GRACE_MS`), so the one number answers both ends.
 */
export const SAFARI_OFF_SETTLE_MS = CAMERA_WAITING_RELEASE_GRACE_MS

/**
 * How long after `webkitpresentationmodechanged` (or the tab coming back) before the window's state is read.
 * That event fires while `document.pictureInPictureElement` still says the opposite, in both directions
 * (measured 2026-09-20), so it is read after the browser has caught up rather than when it is raised.
 */
export const PRESENTATION_SETTLE_MS = 250

/**
 * The floating window, as `requestPictureInPicture()` resolves with it and `enterpictureinpicture` carries it.
 * Typed loosely because either can be missing in an engine that implements only part of the spec.
 */
type PipWindowLike = {
  width: number
  height: number
  addEventListener: (type: 'resize', listener: EventListener) => void
  removeEventListener: (type: 'resize', listener: EventListener) => void
}

function asPipWindow(value: unknown): PipWindowLike | null {
  const win = value as Partial<PipWindowLike> | null | undefined
  const listens =
    typeof win?.addEventListener === 'function' && typeof win?.removeEventListener === 'function'
  return listens ? (win as PipWindowLike) : null
}

/**
 * SC-158: our own live camera picture during a None-to-effect hold (`readLocalCameraHoldPreview`), shaped
 * like a track reference so the renderer treats it like any other camera. Local display only: this element
 * is the one place it is ever attached.
 */
export function holdPreviewTrackRef(preview: MediaStreamTrack): TrackReference {
  return streamTrackRef(preview)
}

function trackOf(ref: TrackReference | undefined): AttachableTrack | null {
  const track = ref?.publication?.track as AttachableTrack | undefined
  return track && typeof track.attach === 'function' ? track : null
}

export class VideoPipRenderer implements PipRenderer {
  private video: HTMLVideoElement | null = null
  /** Owns the element's real `srcObject`; LiveKit only ever sees a facade of it. */
  private source: PipElementSource | null = null
  private attached: TrackReference | undefined
  private latestModel: PipModel | null = null
  private open_ = false
  /**
   * Between `requestPictureInPicture()` and `enterpictureinpicture`: the window is appearing, so the element
   * is already someone's source and the open rules apply to it. `requested` while the promise is pending,
   * `queued` once it resolved without the window being up (WebKit raises the event as a later task).
   */
  private opening: 'requested' | 'queued' | null = null

  /** What the element shows, as `pickSafariPicture` decided it; the next pick may keep its place. */
  private pick: SafariPick | undefined
  /** One-shot: the grace for a remote camera that went off ran out, so the next pick lets it go. */
  private offSettled = false
  private offSettleTimer: ReturnType<typeof setTimeout> | null = null
  private recheckTimer: ReturnType<typeof setTimeout> | null = null
  private neutralPictures: NeutralPictures | null = null

  private readonly changeListeners = new Set<() => void>()
  private readonly closedListeners = new Set<(by: PipClosedBy) => void>()
  private teardowns: Array<() => void> = []

  /** Tells a close we asked for apart from one the user made with the window's own button. */
  private closingOurselves = false

  /** The open window whose size the element is following, or `null` while closed. */
  private pipWindow: PipWindowLike | null = null
  private readonly onWindowResize: EventListener = () => this.sizeToWindow()

  constructor(private readonly doc: Document = document) {
    this.mount()
  }

  get isOpen(): boolean {
    return this.open_
  }

  /** For the tests, and for anyone debugging a window that opened showing the wrong person. */
  get attachedTrack(): TrackReference | undefined {
    return this.attached
  }

  get model(): PipModel | null {
    return this.latestModel
  }

  /** What the element actually plays. `video.srcObject` is LiveKit's view of it, see `PipElementSource`. */
  get displayedStream(): MediaStream | null {
    return this.source?.displayed ?? null
  }

  /**
   * Exactly the condition `open()` refuses on, read off the element's real attach state rather than
   * re-derived from the model, so the call-bar button can never be enabled for a click that is bound to fail.
   */
  get canOpen(): boolean {
    return Boolean(this.video && this.attached)
  }

  /**
   * Nothing but the request. No attach, no `play()`, no await before it — each of those either resets
   * `readyState` or spends the transient activation, and Safari needs both intact.
   */
  async open(model: PipModel, _opts: { reason: PipOpenReason }): Promise<void> {
    this.latestModel = model
    const video = this.video
    if (!video) throw new Error('The picture-in-picture video element is gone')
    if (this.open_) return

    if (!this.attached) {
      throw new Error('Nothing to show in picture-in-picture yet - no camera or screen share is live')
    }

    // Deliberately not rescued. A retry without a gesture fails too, so the honest thing is to say why in a
    // log a production report can be grepped for rather than to loop. P2 showed this does not happen when
    // the element has been attached since the renderer was built, which it has.
    // The window is appearing from here on. Without this a model update in the ~100 ms before the event
    // takes the closed rules, which may empty the element under a window Safari is about to show.
    // A click in the gap of an earlier request that has not shown its window yet (`queued`) is a second
    // request on top of it. The earlier one still owns `opening`: this one's refusal says nothing about the
    // window that is appearing, and running the closed rules for it would empty the element under it.
    const joining = this.opening !== null
    if (!joining) this.opening = 'requested'
    let win: unknown
    try {
      win = await video.requestPictureInPicture()
    } catch (error) {
      if (joining) {
        // Not a no-op for a click that finds the state stuck: the re-check lets go of a queued open the
        // mode says has no window, so clicking is still a way out of it.
        this.recheckSoon()
      } else {
        // No window is coming. The closed rules apply again to whatever was held back meanwhile.
        this.opening = null
        this.follow()
      }
      throw error
    }
    // Engines disagree on whether this resolves before or after `enterpictureinpicture`; whichever brings the
    // window first starts the sizing, and the other is a no-op.
    this.followWindow(asPipWindow(win))
    if (!joining && !this.open_ && this.opening === 'requested') {
      // The event is still queued. Kept until it arrives, but not for ever: the re-check lets go of it if
      // the mode says there is no window (a request resolved for a window that was closed in between).
      this.opening = 'queued'
      this.recheckSoon()
    }
  }

  /**
   * §2.3: called on every model change, open or not, and on Safari that is the load-bearing half.
   *
   * While closed it is what keeps the element attached and playing so a later click can succeed. While open
   * it is the swap: P2b measured that replacing the source of an element already in picture-in-picture keeps
   * the window open, keeps `document.pictureInPictureElement` true and keeps `readyState` at 4, with the
   * picture visibly changing. No gesture is needed for a swap — only for the first open.
   */
  update(model: PipModel): void {
    this.latestModel = model
    this.follow()
  }

  close(): void {
    if (!this.open_) return
    // The button reads `isOpen`, so a click here may be on a window that is already gone. Asking the browser
    // to exit it is refused (`NotAllowedError`), and nothing else would ever settle the state. Settled in
    // the click itself, which also keeps the click's gesture for a reopen (see `usePictureInPicture.close`).
    if (!this.inWindow()) {
      this.handleClosed(true)
      return
    }
    this.closingOurselves = true

    const exit = (this.doc as Document & { exitPictureInPicture?: () => Promise<void> }).exitPictureInPicture
    if (typeof exit !== 'function') {
      // No way to ask; treat it as already gone rather than leaving `isOpen` lying.
      this.handleClosed()
      return
    }

    void exit
      .call(this.doc)
      // `leavepictureinpicture` is what normally settles this; if it was lost, the re-check does.
      .then(() => this.recheckSoon())
      .catch(() => {
        // Refused. Either the window is already gone, in which case no event is ever coming and nothing
        // else would settle `isOpen` (the button would read "open" and every click would land here), or it
        // is genuinely still up and stays open.
        this.closingOurselves = false
        this.reconcile()
      })
  }

  onClosed(cb: (by: PipClosedBy) => void): () => void {
    this.closedListeners.add(cb)
    return () => this.closedListeners.delete(cb)
  }

  subscribe(cb: () => void): () => void {
    this.changeListeners.add(cb)
    return () => this.changeListeners.delete(cb)
  }

  /** Always `null`: there is no second document on Safari, so React has nowhere to portal to. */
  getSurface(): PipSurface | null {
    return null
  }

  /**
   * Leaving the room takes the element with it. Without this, every join would leave another hidden `<video>`
   * behind holding a reference to a track that is no longer published.
   */
  destroy(): void {
    // No model, so the settle below does not pick a new picture for an element that is about to go.
    this.latestModel = null
    // Settle first, or the close is never reported. `close()` asks the browser and waits for
    // `leavepictureinpicture`; the teardown below removes that listener, so on the leave-the-room path the
    // event can never arrive and `APP_pip_closed` would simply never be recorded. `DocumentPipRenderer`
    // settles synchronously in `close()`, and the two halves of the pair have to agree.
    this.handleClosed()
    this.clearTimer('recheckTimer')
    this.clearTimer('offSettleTimer')
    this.releaseWindow()
    this.syncTrack(undefined)
    this.neutralPictures?.destroy()
    this.neutralPictures = null
    this.source?.destroy()
    this.source = null

    const teardowns = this.teardowns
    this.teardowns = []
    for (const teardown of teardowns) teardown()

    this.video?.remove()
    this.video = null
    this.open_ = false
    this.opening = null
    this.changeListeners.clear()
    this.closedListeners.clear()
  }

  private mount(): void {
    const video = this.doc.createElement('video')
    video.autoplay = true
    // Muted is not a preference: an unmuted autoplay is refused, and the audio comes from LiveKit's own
    // audio elements in the page, never from this one.
    video.muted = true
    video.setAttribute('playsinline', '')
    video.setAttribute('aria-hidden', 'true')
    video.setAttribute('data-sevv-pip', 'safari-video')
    Object.assign(video.style, HIDDEN_VIDEO_STYLE)

    this.doc.body.appendChild(video)
    this.video = video
    this.source = new PipElementSource(video)

    // The standard events decide. `webkitpresentationmodechanged` fires while
    // `document.pictureInPictureElement` still says the opposite, in both directions (measured 2026-09-20),
    // so believing it would make `isOpen` wrong for a frame in a component that re-renders on it.
    this.listen(video, 'enterpictureinpicture', (event) => {
      this.open_ = true
      this.opening = null
      // The check scheduled for a queued open has its answer; a later one starts from a real prompt.
      this.clearTimer('recheckTimer')
      const carried = (event as Event & { pictureInPictureWindow?: unknown }).pictureInPictureWindow
      this.followWindow(asPipWindow(carried))
      this.changeListeners.forEach((listener) => listener())
    })
    this.listen(video, 'leavepictureinpicture', () => this.handleClosed())
    // Prompts only: a window can be closed by Safari without `leavepictureinpicture` ever reaching us.
    this.listen(video, 'webkitpresentationmodechanged', () => this.recheckSoon())
    this.listen(this.doc, 'visibilitychange', () => {
      if (this.doc.visibilityState === 'visible') this.recheckSoon()
    })
  }

  /**
   * Re-decides what the element shows from the latest model, and brings the element to it.
   *
   * The slot logic in `pickSafariPicture` only applies to an open window: a closed one has nobody looking
   * at it, so it follows the plain pick and an emptied element costs nothing but a disabled button.
   */
  private follow(): void {
    const model = this.latestModel
    if (!model) return
    const could = this.canOpen

    const open =
      this.windowUp && this.pick ? { shown: this.pick, offSettled: this.offSettled } : undefined
    const pick = pickSafariPicture(model, open)
    this.offSettled = false
    this.pick = pick
    this.watchSettling(pick)

    this.syncTrack(this.refFor(pick))
    // Only on a flip: a speaker change swaps one attached track for another, which the button does not see.
    if (this.canOpen !== could) this.changeListeners.forEach((listener) => listener())
  }

  /** Open, or about to be: either way somebody is about to look at the element, so it may not go empty. */
  private get windowUp(): boolean {
    return this.open_ || this.opening !== null
  }

  /**
   * The reference to attach for a pick. For an open window that is never `undefined` while the browser can
   * capture a canvas: a held camera gets the waiting picture, and nothing at all, or a pick whose track
   * LiveKit has already taken away (a stale model, `unpublishTrack` sets it to undefined), gets the idle
   * one, because an empty source closes the window. `undefined` where the browser cannot capture a canvas,
   * in which case the element is emptied as it was before SC-476 (an empty window is still better than a
   * held camera's last frame), and for a closed window, which has nothing to close.
   */
  private refFor(pick: SafariPick | undefined): TrackReference | undefined {
    if (pick?.kind === 'track' && trackOf(pick.trackRef)) return pick.trackRef
    if (!this.windowUp || !this.video) return undefined

    const kind = pick?.kind === 'waiting' && !pick.settling ? 'waiting' : 'idle'
    // The size of what is on the element now, so the window keeps its shape through the swap.
    const size = { width: this.video.videoWidth, height: this.video.videoHeight }
    this.neutralPictures ??= createNeutralPictures(this.doc)
    return this.neutralPictures.refFor(kind, size)
  }

  /** One timer while the pick is a camera that might be a hold; see `SAFARI_OFF_SETTLE_MS`. */
  private watchSettling(pick: SafariPick | undefined): void {
    if (!(pick?.kind === 'waiting' && pick.settling)) {
      this.clearTimer('offSettleTimer')
      return
    }
    if (this.offSettleTimer !== null) return
    this.offSettleTimer = setTimeout(() => {
      this.offSettleTimer = null
      this.offSettled = true
      this.follow()
    }, SAFARI_OFF_SETTLE_MS)
  }

  /**
   * Brings the element to `next` without ever emptying it, and wraps the change in the WebKit 262479
   * workaround.
   *
   * **Make before break, on a stream LiveKit cannot reach.** The incoming track goes into the real stream
   * (`PipElementSource.show`) before the outgoing one comes out. `attach` and `detach` are still called, but
   * only to register and unregister the element with LiveKit (adaptive stream: size, and the picture-in-picture
   * visibility that keeps the video flowing in a hidden tab); what they do to `video.srcObject` lands on the
   * facade LiveKit is given, not on what the element plays. Before SC-476 they were what swapped the picture,
   * and LiveKit's own detach on every remote mute emptied and nulled the source under an open window.
   * Before that this cleared the element on every swap ("SC-158: the outgoing picture could linger until the
   * new one's first frame"); the held camera's picture is now replaced inside the stream instead, by a
   * picture that has already been drawn (`neutralPicture`), so the old frame has nothing to linger behind.
   * Only when there is nothing to show at all, which is only ever a closed window, is the element cleared.
   *
   * **WebKit 262479**: toggling `controls` on either side of a source change is what makes Safari re-read
   * the element. It was fixed upstream in October 2023 and our floor is Safari 18, so this is belt and braces
   * - but it is two assignments, and the failure it prevents is a window that silently keeps showing the
   * previous person.
   */
  private syncTrack(next: TrackReference | undefined): void {
    const video = this.video
    if (!video) return

    const current = trackOf(this.attached)
    const incoming = trackOf(next)
    if (current === incoming) {
      // Same underlying track. A rebuilt reference around the same media is not a reason to tear the
      // picture down and put it back.
      this.attached = next
      return
    }

    video.controls = true
    try {
      // On the real stream first, so that the `play()` LiveKit's own attach makes finds a source. A LiveKit
      // track then adds itself to the facade as well, which `PipElementSource` mirrors as a no-op.
      this.source?.show(incoming?.mediaStreamTrack)
      incoming?.attach(video)
      current?.detach(video)
      if (incoming) {
        // Never awaited. `autoplay` usually starts it anyway, and an await here would put a promise between
        // a future click and `requestPictureInPicture()`.
        void video.play?.()?.catch?.(() => {})
      } else {
        this.source?.clear()
      }
    } finally {
      video.controls = false
    }

    this.attached = next
  }

  /**
   * Lays the element out at the floating window's size for as long as the window is up.
   *
   * livekit-client's adaptive stream (on, `mediaConfig.ts`) picks the layer it asks the server for from the
   * attached elements' `clientWidth`/`clientHeight`, and counts picture-in-picture only as "visible", not as
   * a size. At 2px the window got whatever the on-page tile asked for: the lowest layer from a sidebar tile,
   * and a share that was not maximised in the tab was unreadable. Opacity, position and `pointer-events` stay
   * as they are, so the element is still invisible and untouchable in the page; adaptive stream ignores
   * opacity. Public APIs only - nothing here reaches into livekit-client.
   *
   * The size is kept across a track swap while the window is up: the element is the window, so the incoming
   * track is asked for at the window's size too.
   */
  private followWindow(win: PipWindowLike | null): void {
    if (!win || !this.video) return
    if (this.pipWindow !== win) {
      this.pipWindow?.removeEventListener('resize', this.onWindowResize)
      this.pipWindow = win
      win.addEventListener('resize', this.onWindowResize)
    }
    this.sizeToWindow()
  }

  private sizeToWindow(): void {
    const video = this.video
    const win = this.pipWindow
    if (!video || !win || !(win.width > 0) || !(win.height > 0)) return
    video.style.width = `${win.width}px`
    video.style.height = `${win.height}px`
  }

  /** Back to the 2px Safari accepts for a later open, with nothing left listening to a closed window. */
  private releaseWindow(): void {
    this.pipWindow?.removeEventListener('resize', this.onWindowResize)
    this.pipWindow = null
    if (!this.video) return
    this.video.style.width = HIDDEN_VIDEO_STYLE.width as string
    this.video.style.height = HIDDEN_VIDEO_STYLE.height as string
  }

  private listen(target: EventTarget, type: string, handler: EventListener): void {
    target.addEventListener(type, handler)
    this.teardowns.push(() => target.removeEventListener(type, handler))
  }

  /**
   * Whether the browser still has this element in a window.
   *
   * `webkitPresentationMode` decides where Safari has it, and `'inline'` means closed whatever
   * `document.pictureInPictureElement` says: after an exit the browser makes itself (an emptied source)
   * Safari leaves `pictureInPictureElement` pointing at the element for good, with the mode back to
   * `'inline'` and no `leavepictureinpicture` (measured 2026-10-24). The standard property is only asked
   * where there is no mode, and where neither exists nothing contradicts `isOpen`, so it stands.
   */
  private inWindow(): boolean {
    const video = this.video
    if (!video) return false
    const mode = (video as HTMLVideoElement & { webkitPresentationMode?: string })
      .webkitPresentationMode
    if (typeof mode === 'string') return mode === 'picture-in-picture'
    if ('pictureInPictureElement' in this.doc) {
      return (this.doc as Document).pictureInPictureElement === video
    }
    return true
  }

  /** What a closed window would follow now is a camera or share, which is something to open onto. */
  private closedPickIsTrack(): boolean {
    return this.latestModel !== null && pickSafariPicture(this.latestModel)?.kind === 'track'
  }

  /** Settles `isOpen` against the browser when no `leavepictureinpicture` said the window went. */
  private reconcile(): void {
    if (this.open_ && !this.inWindow()) {
      this.handleClosed()
    } else if (!this.open_ && this.opening === 'queued' && !this.inWindow()) {
      // The request resolved and no window ever came; do not hold the open rules for one.
      this.opening = null
      this.follow()
    }
  }

  private recheckSoon(): void {
    // Only a request that has resolved is re-checked: while it is pending the mode is still `inline`
    // however slow Safari is, and that must not read as "no window".
    if (!(this.open_ || this.opening === 'queued') || this.recheckTimer !== null) return
    this.recheckTimer = setTimeout(() => {
      this.recheckTimer = null
      this.reconcile()
    }, PRESENTATION_SETTLE_MS)
  }

  private clearTimer(name: 'offSettleTimer' | 'recheckTimer'): void {
    const timer = this[name]
    if (timer !== null) clearTimeout(timer)
    this[name] = null
  }

  /**
   * `deferFollow` is for the one caller that runs inside a click which is about to ask for the window again
   * (`close()` on a window Safari had already closed): Safari needs the element untouched until
   * `requestPictureInPicture()` has been called in that click, and the follow below swaps the picture
   * (show, attach, play). It runs as a microtask instead, after the hook's request in the same click.
   */
  private handleClosed(deferFollow = false): void {
    if (!this.open_) {
      // A leave (or a destroy) with no window up: an open still appearing is over, and the element goes back
      // to the closed rules rather than holding a neutral picture for a window that is not coming.
      if (this.opening !== null) {
        this.opening = null
        this.follow()
      }
      return
    }

    this.opening = null
    const by: PipClosedBy = this.closingOurselves ? 'user' : 'browser'
    this.closingOurselves = false
    this.open_ = false
    this.clearTimer('recheckTimer')
    this.clearTimer('offSettleTimer')
    this.releaseWindow()

    // The element stays attached and playing. It is what a second open needs to still be holding, and
    // nothing is on screen while the window is closed. What it holds follows the closed rules again, so a
    // window that closed on a held camera's waiting picture does not leave a button that opens onto it.
    if (deferFollow && this.closedPickIsTrack()) {
      // Only where there is a camera to go back to: with none, the follow empties the element, `canOpen`
      // turns false and nothing is requested, so there is no click to protect.
      // No place is kept for the reopened window: it goes to the camera the closed rules choose, as it did
      // when this follow ran inline.
      this.pick = undefined
      queueMicrotask(() => this.follow())
    } else {
      this.follow()
    }
    this.changeListeners.forEach((listener) => listener())
    this.closedListeners.forEach((listener) => listener(by))
  }
}

export default VideoPipRenderer
